import { useAuthStore } from '@/store/authStore'
import { useUserStore } from '@/store/userStore'
import { logger } from '@/utils/logger'

/**
 * 네이티브에 토큰 갱신 요청
 *
 * @returns 새로운 Access Token
 * @throws 토큰 갱신 실패 시 에러
 */
export async function requestTokenRefreshFromNative(): Promise<string> {
  logger.token('네이티브에 토큰 갱신 요청')

  // WebView 환경 확인
  if (!window.ReactNativeBridge) {
    throw new Error('WebView 환경이 아닙니다')
  }

  return new Promise((resolve, reject) => {
    // 타임아웃 설정 (30초)
    const timeout = setTimeout(() => {
      cleanup()
      logger.error('토큰 갱신 타임아웃 - 네트워크를 확인해주세요')
      reject(new Error('토큰 갱신 요청이 시간 초과되었습니다. 네트워크를 확인해주세요.'))
    }, 30000)

    // 응답 리스너 등록
    const cleanup = (() => {
      let cleanupFn: (() => void) | null = null

      cleanupFn = (() => {
        if (!window.ReactNativeBridge) return () => {}

        const handler = (message: { type: string; data: any }) => {
          if (message.type === 'AUTH:REFRESH_RESPONSE' || message.type === 'AUTH:TOKEN_REFRESHED') {
            clearTimeout(timeout)
            if (cleanupFn) cleanupFn()

            logger.token('토큰 갱신 응답:', message.data)

            if (!message.data.success || !message.data.accessToken) {
              reject(new Error(message.data.error || '토큰 갱신 실패'))
              return
            }

            // 메모리에 새 토큰 저장
            useAuthStore.getState().setAccessToken(message.data.accessToken)

            logger.success('토큰 갱신 완료')
            resolve(message.data.accessToken)
          }
        }

        window.ReactNativeBridge.onMessage(handler)
        return () => {}
      })()

      return cleanupFn
    })()

    // 메시지 전송
    try {
      window.ReactNativeBridge!.sendMessage('AUTH:REFRESH_REQUEST', {})
    } catch (error: any) {
      clearTimeout(timeout)
      cleanup()
      logger.error('토큰 갱신 실패:', error)
      reject(error)
    }
  })
}

/**
 * 네이티브로부터 AUTH:SIGNED_IN 메시지 수신 시 호출
 *
 * @param accessToken - 네이티브에서 받은 Access Token
 * @param userInfo - 사용자 정보 (옵션)
 */
export function handleSignedInFromNative(accessToken: string, userInfo?: any) {
  logger.auth('🔐 네이티브로부터 로그인 토큰 수신')
  logger.debug('accessToken:', accessToken ? `${accessToken.substring(0, 20)}...` : 'null')
  logger.debug('userInfo:', userInfo)

  // 현재 URL 확인 (새로고침 감지)
  const currentUrl = window.location.pathname
  const isRefreshed = currentUrl !== '/auth/login' && currentUrl !== '/'
  logger.info(`📍 현재 페이지: ${currentUrl} ${isRefreshed ? '(새로고침 감지)' : ''}`)

  const authStore = useAuthStore.getState()

  // 메모리에 토큰 저장
  logger.info('💾 메모리에 토큰 저장 중...')
  authStore.setAccessToken(accessToken)
  logger.success('✅ accessToken 저장 완료')

  // 사용자 정보 저장
  if (userInfo) {
    logger.info('💾 사용자 정보 저장 중...')

    // 네이티브에서 오는 필드명 변환
    const convertedUserInfo: any = {
      user_id: userInfo.id || userInfo.user_id || 0,           // id → user_id
      nickname: userInfo.name || userInfo.nickname || '',       // name → nickname
      profile_image: userInfo.profile_image || null,
      provider: userInfo.provider || null,
      created_at: userInfo.created_at || null,
      is_agree: userInfo.is_agree || false,
      // userStore에 필요한 추가 필드들 (기본값)
      type16_id: userInfo.type16_id || null,
      sex_code: userInfo.sex_code || null,
      school_name: userInfo.school_name || null,
      school_code: userInfo.school_code || null,
      school_level: userInfo.school_level || null,
      birthday: userInfo.birthday || null,
      grade_code: userInfo.grade_code || null,
      mbti: userInfo.mbti || null,
      join_path: userInfo.join_path || null,
      join_key: userInfo.join_key || null,
      consult_style: userInfo.consult_style || null,
      email: userInfo.email || null
    }

    // authStore에 원본 저장
    authStore.setUserInfo(userInfo)

    // userStore에 변환된 정보 저장 (MyPage에서 사용)
    const userStore = useUserStore.getState()
    userStore.setUserInfo(convertedUserInfo)

    logger.success('✅ userInfo 저장 완료:', {
      user_id: convertedUserInfo.user_id,
      nickname: convertedUserInfo.nickname,
      is_agree: convertedUserInfo.is_agree,
      authStore: '원본 저장',
      userStore: '변환된 데이터 저장'
    })
  } else {
    logger.warn('⚠️ 사용자 정보 없음')
  }

  logger.success('✅ 토큰 및 사용자 정보 메모리 저장 완료')

  // 저장된 토큰 확인
  const savedToken = authStore.getAccessToken()
  logger.info('✅ 저장 확인:', savedToken ? '토큰 존재' : '❌ 토큰 없음')
}

/**
 * 네이티브로부터 AUTH:SIGNED_OUT 메시지 수신 시 호출
 *
 * @param reason - 로그아웃 사유
 */
export function handleSignedOutFromNative(reason?: string) {
  logger.auth('네이티브로부터 로그아웃 알림:', reason)

  const authStore = useAuthStore.getState()
  authStore.clearAuth()

  logger.success('메모리 인증 정보 초기화 완료')

  // 로그인 페이지로 이동
  window.location.href = '/auth/login'
}

/**
 * 네이티브로부터 AUTH:TOKEN_REFRESHED 메시지 수신 시 호출
 *
 * @param accessToken - 갱신된 Access Token
 */
export function handleTokenRefreshedFromNative(accessToken: string) {
  logger.token('네이티브로부터 갱신된 토큰 수신')

  const authStore = useAuthStore.getState()
  authStore.setAccessToken(accessToken)

  logger.success('갱신된 토큰 메모리 저장 완료')
}

/**
 * 네이티브에 토큰 백업 요청
 *
 * @param accessToken - Access Token
 * @param refreshToken - Refresh Token
 * @param userInfo - 사용자 정보
 */
export function backupTokensToNative(accessToken: string, refreshToken: string, userInfo: any): void {
  if (!window.ReactNativeBridge) {
    logger.warn('❌ 웹 브라우저 환경 - 토큰 백업 건너뜀')
    logger.info('window.ReactNativeWebView:', !!window.ReactNativeWebView)
    return
  }

  try {
    logger.auth('🔐 네이티브에 토큰 백업 요청')
    logger.debug('accessToken:', accessToken ? `${accessToken.substring(0, 20)}...` : 'null')
    logger.debug('refreshToken:', refreshToken ? `${refreshToken.substring(0, 20)}...` : 'null')
    logger.debug('userInfo:', {
      user_id: userInfo?.user_id,
      nickname: userInfo?.nickname,
      is_agree: userInfo?.is_agree
    })

    window.ReactNativeBridge.sendMessage('backupTokens', {
      accessToken,
      refreshToken,
      userInfo
    })

    logger.success('✅ 네이티브 토큰 백업 요청 전송 완료')
    logger.info('📱 RN이 Keychain에 저장 중...')
  } catch (error) {
    logger.error('❌ 네이티브 토큰 백업 실패:', error)
    // 백업 실패해도 로그인은 진행 (메모리에 토큰 있음)
  }
}
